import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Loader2, Copy, Sparkles, MessageCircle, Wand2, Mic } from "lucide-react";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import ExportMenu from "@/components/ExportMenu";
import QuickActionBar from "@/components/QuickActionBar";

const TOOLS = [
  { key: "lesson_plan", label: "Lesson Plan" },
  { key: "mcq", label: "MCQs" },
  { key: "worksheet", label: "Worksheet" },
  { key: "rubric", label: "Rubric" },
  { key: "homework", label: "Homework" },
];

const LANGS = ["English", "Hindi", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Arabic", "French", "Spanish"];
const CURRICULA = ["CBSE", "ICSE", "IB", "Cambridge", "State board", "US", "UK"];
// Stable reference — declared once at module scope so React doesn't see a new array on every render.
const QUICKBAR_EXCLUDE_TOOLS = ["lesson", "mcq", "worksheet"];

export default function TeachingAssistant() {
  const [params] = useSearchParams();
  const initialTool = params.get("tool");
  const [tab, setTab] = useState(initialTool && TOOLS.find((t) => t.key === initialTool) ? "tools" : "chat");

  return (
    <Layout title="AI Teaching Assistant">
      <QuickActionBar exclude={QUICKBAR_EXCLUDE_TOOLS} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="chat" className="rounded-full px-4" data-testid="tab-chat">
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Chat
          </TabsTrigger>
          <TabsTrigger value="tools" className="rounded-full px-4" data-testid="tab-tools">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Quick generators
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="mt-5">
          <ChatPanel />
        </TabsContent>
        <TabsContent value="tools" className="mt-5">
          <ToolsPanel initialTool={initialTool} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function ChatPanel() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! Ask me to plan a lesson, generate MCQs, write a worksheet, or explain any concept. Try: *Plan a 45-minute Grade 8 lesson on Photosynthesis (CBSE).*",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scroll = useRef(null);

  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId,
        message: text,
        kind: "general",
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.assistant_message.content }]);
    } catch {
      toast.error("AI request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div ref={scroll} className="tb-card p-6 max-h-[60vh] min-h-[420px] overflow-auto space-y-4" data-testid="chat-stream">
        {messages.map((m, i) => (
          <div key={m.id || `${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
              m.role === "user"
                ? "bg-violet-600 text-white rounded-br-md"
                : "bg-muted rounded-bl-md"
            }`}>
              {m.role === "assistant" ? <Markdown>{m.content}</Markdown> : <p className="whitespace-pre-wrap">{m.content}</p>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-muted px-4 py-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> generating…
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Ask anything teaching-related…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          className="h-11 rounded-full"
          data-testid="chat-input"
        />
        <Button
          onClick={send}
          disabled={busy || !input.trim()}
          className="h-11 rounded-full"
          data-testid="chat-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function ToolsPanel({ initialTool }) {
  const [tool, setTool] = useState(initialTool && TOOLS.find((t) => t.key === initialTool) ? initialTool : "lesson_plan");
  const [form, setForm] = useState({
    subject: "Science",
    grade: "8",
    topic: "Photosynthesis",
    duration_minutes: 45,
    curriculum: "CBSE",
    objectives: "",
    count: 10,
    difficulty: "medium",
    question_count: 8,
    types: "short, long, fill",
    assignment_title: "End of unit project",
    criteria_count: 4,
    language: "English",
  });
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setOutput(null);
    try {
      let url = "";
      let payload = {};
      if (tool === "lesson_plan") {
        url = "/ai/generate/lesson-plan";
        payload = {
          subject: form.subject,
          grade: form.grade,
          topic: form.topic,
          duration_minutes: parseInt(form.duration_minutes) || 45,
          curriculum: form.curriculum,
          objectives: form.objectives || null,
          language: form.language,
        };
      } else if (tool === "mcq") {
        url = "/ai/generate/mcq";
        payload = {
          subject: form.subject,
          grade: form.grade,
          topic: form.topic,
          count: parseInt(form.count) || 10,
          difficulty: form.difficulty,
          language: form.language,
        };
      } else if (tool === "worksheet") {
        url = "/ai/generate/worksheet";
        payload = {
          subject: form.subject,
          grade: form.grade,
          topic: form.topic,
          question_count: parseInt(form.question_count) || 8,
          types: form.types.split(",").map((s) => s.trim()).filter(Boolean),
          language: form.language,
        };
      } else if (tool === "rubric") {
        url = "/ai/generate/rubric";
        payload = {
          subject: form.subject,
          grade: form.grade,
          assignment_title: form.assignment_title,
          criteria_count: parseInt(form.criteria_count) || 4,
          language: form.language,
        };
      } else if (tool === "homework") {
        url = "/ai/generate/homework";
        payload = {
          subject: form.subject,
          grade: form.grade,
          topic: form.topic,
          duration_minutes: parseInt(form.duration_minutes) || 30,
          language: form.language,
        };
      }
      const { data } = await api.post(url, payload);
      setOutput(data);
      toast.success(`${data.title} generated!`);
    } catch {
      toast.error("Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copyOutput = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output.content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 tb-card p-6 space-y-4" data-testid="tools-panel">
        <div>
          <Label>Tool</Label>
          <Select value={tool} onValueChange={setTool}>
            <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="tool-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TOOLS.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {tool !== "rubric" && (
          <div>
            <Label>Topic</Label>
            <Input
              className="mt-1.5 h-11 rounded-xl"
              value={form.topic}
              onChange={(e) => update("topic", e.target.value)}
              data-testid="tool-topic"
            />
          </div>
        )}
        {tool === "rubric" && (
          <div>
            <Label>Assignment title</Label>
            <Input
              className="mt-1.5 h-11 rounded-xl"
              value={form.assignment_title}
              onChange={(e) => update("assignment_title", e.target.value)}
              data-testid="tool-assignment-title"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Subject</Label>
            <Input
              className="mt-1.5 h-11 rounded-xl"
              value={form.subject}
              onChange={(e) => update("subject", e.target.value)}
              data-testid="tool-subject"
            />
          </div>
          <div>
            <Label>Grade</Label>
            <Input
              className="mt-1.5 h-11 rounded-xl"
              value={form.grade}
              onChange={(e) => update("grade", e.target.value)}
              data-testid="tool-grade"
            />
          </div>
        </div>

        {tool === "lesson_plan" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  className="mt-1.5 h-11 rounded-xl"
                  value={form.duration_minutes}
                  onChange={(e) => update("duration_minutes", e.target.value)}
                />
              </div>
              <div>
                <Label>Curriculum</Label>
                <Select value={form.curriculum} onValueChange={(v) => update("curriculum", v)}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRICULA.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Learning objectives (optional)</Label>
              <Textarea
                className="mt-1.5 rounded-xl"
                rows={3}
                value={form.objectives}
                onChange={(e) => update("objectives", e.target.value)}
              />
            </div>
          </>
        )}

        {tool === "mcq" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Count</Label>
              <Input
                type="number"
                className="mt-1.5 h-11 rounded-xl"
                value={form.count}
                onChange={(e) => update("count", e.target.value)}
              />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => update("difficulty", v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["easy", "medium", "hard", "mixed"].map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {tool === "worksheet" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label># Questions</Label>
              <Input
                type="number"
                className="mt-1.5 h-11 rounded-xl"
                value={form.question_count}
                onChange={(e) => update("question_count", e.target.value)}
              />
            </div>
            <div>
              <Label>Types (comma)</Label>
              <Input
                className="mt-1.5 h-11 rounded-xl"
                value={form.types}
                onChange={(e) => update("types", e.target.value)}
              />
            </div>
          </div>
        )}

        {tool === "rubric" && (
          <div>
            <Label>Number of criteria</Label>
            <Input
              type="number"
              className="mt-1.5 h-11 rounded-xl"
              value={form.criteria_count}
              onChange={(e) => update("criteria_count", e.target.value)}
            />
          </div>
        )}

        {tool === "homework" && (
          <div>
            <Label>Estimated time (min)</Label>
            <Input
              type="number"
              className="mt-1.5 h-11 rounded-xl"
              value={form.duration_minutes}
              onChange={(e) => update("duration_minutes", e.target.value)}
            />
          </div>
        )}

        <div>
          <Label>Output language</Label>
          <Select value={form.language} onValueChange={(v) => update("language", v)}>
            <SelectTrigger className="mt-1.5 h-11 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          onClick={submit}
          disabled={busy}
          className="w-full h-11 rounded-full"
          data-testid="generate-tool-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate</>}
        </Button>
      </div>

      <div className="lg:col-span-3 tb-card p-6 min-h-[480px] relative" data-testid="tool-output">
        {!output && !busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
            <div>
              <Sparkles className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 font-display">Your AI-generated content will appear here.</p>
            </div>
          </div>
        )}
        {busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
            <div>
              <Loader2 className="h-8 w-8 animate-spin mx-auto" />
              <p className="mt-3 font-display">TeeBee is thinking…</p>
            </div>
          </div>
        )}
        {output && (
          <>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{output.kind.replace(/_/g, " ")}</p>
                <h3 className="mt-1 font-display text-xl tracking-tight font-medium">{output.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <ExportMenu title={output.title} content={output.content} includePptx={output.kind === "lesson_plan" || output.kind === "worksheet"} />
                <Button onClick={copyOutput} variant="outline" size="sm" className="rounded-full" data-testid="copy-output">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
              </div>
            </div>
            <Markdown>{output.content}</Markdown>
          </>
        )}
      </div>
    </div>
  );
}
